import React, {
  useRef,
  useState,
  useEffect,
} from "react";
import CommonButton from "../../../components/CommonButton";
import SelectPetsSection from "../../../components/customer/SelectPetsSection";

import {
  catbreedOptionsMap,
  dogbreedOptionsMap,
  getBookingQuestions,
  petOptions,
} from "../../../utils/Customer/CustomerData";

import { useNavigate } from "react-router-dom";

import {
  MapPin,
  Check,
  Calculator,
  Loader2,
} from "lucide-react";

import TextInputComp from "../../../components/customer/TextInputComp";

import Autocomplete from "react-google-autocomplete";

import QuestionCard, {
  QuestionType,
} from "../../../components/customer/QuestionCard";

import { useAlert } from "../../../components/customer/AlertContext";

import { MAPS_KEY } from "../../../config/apiUrlsCustomer";

import {
  GoogleMap,
  useJsApiLoader,
  MarkerF,
} from "@react-google-maps/api";

import {
  IPetTaxiPriceCalculateRequest,
  ICreatePetProfileRequest,
} from "../../../types/Request/CustomerRequest";

import {
  useCreateUserProfileMutation,
  useLazyGetPetProfileQuery,
  useUploadCustomerFileMutation,
} from "../../../redux/actions/Customer/profileCustomer";

import {
  ICalculatePettaxiPriceResponse,
  PetData,
} from "../../../types/Response/CustomerResponse";

import {
  useCalculatePetTaxiPriceMutation,
} from "../../../redux/actions/Customer/serviceCustomer";

import { useSelector } from "react-redux";

import { RootState } from "../../../redux/rootReducer";

// ============================================================
// DOG AVATARS
// ============================================================

const DOG_CARTOON_AVATARS = [
  {
    id: "dog-1",
    label: "Happy Pup",
    value:
      "https://images.unsplash.com/photo-1543466835-00a7907e9de1?w=300&auto=format&fit=crop&q=80",
  },
  {
    id: "dog-2",
    label: "Playful Beagle",
    value:
      "https://images.unsplash.com/photo-1537151608828-ea2b11777ee8?w=300&auto=format&fit=crop&q=80",
  },
  {
    id: "dog-3",
    label: "Cute Golden",
    value:
      "https://images.unsplash.com/photo-1583511655857-d19b40a7a54e?w=300&auto=format&fit=crop&q=80",
  },
  {
    id: "dog-4",
    label: "Fluffy Corgi",
    value:
      "https://images.unsplash.com/photo-1612774412771-005ed8e861d2?w=300&auto=format&fit=crop&q=80",
  },
];

// ============================================================
// CAT AVATARS
// ============================================================

const CAT_CARTOON_AVATARS = [
  {
    id: "cat-1",
    label: "Cute Kitty",
    value:
      "https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?w=300&auto=format&fit=crop&q=80",
  },
  {
    id: "cat-2",
    label: "Playful Cat",
    value:
      "https://images.unsplash.com/photo-1573865526739-10659fec78a5?w=300&auto=format&fit=crop&q=80",
  },
  {
    id: "cat-3",
    label: "Fluffy Ginger",
    value:
      "https://images.unsplash.com/photo-1533738363-b7f9aef128ce?w=300&auto=format&fit=crop&q=80",
  },
  {
    id: "cat-4",
    label: "Sleepy Kitty",
    value:
      "https://images.unsplash.com/photo-1561948955-570b270e7c36?w=300&auto=format&fit=crop&q=80",
  },
];

// ============================================================
// BREED SIZE MAPPING
// ============================================================
//
// IMPORTANT:
//
// This is NOT calculated from weight.
//
// The user selects:
//
// toy     -> toy
// small   -> small
// medium  -> medium
// large   -> large
// xl      -> giant
//
// ============================================================

const BREED_SIZE_MAP: Record<string, string> = {

  small: "small",
  medium: "medium",
  large: "large",
  xl: "Extra Large",
};

// ============================================================
// MAP
// ============================================================

const containerStyle = {
  width: "100%",
  height: "150px",
  minHeight: "150px",
  display: "block",
  borderRadius: "8px",
};

const defaultCenter = {
  lat: 25.2048,
  lng: 55.2708,
};

// ============================================================
// COMPONENT
// ============================================================

const PetTaxiScreen: React.FC = () => {
  const { showConfirm } = useAlert();

  const navigate = useNavigate();

  const isLoggedIn = useSelector(
    (state: RootState) =>
      state.auth.customer.isLoggedIn
  );

  // ==========================================================
  // GOOGLE MAP
  // ==========================================================

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: MAPS_KEY,
    libraries: ["places"],
  });

  // ==========================================================
  // REDUX
  // ==========================================================

  const [
    calculatePetTaxiPrice,
    { isLoading: isCalculatingPrice },
  ] = useCalculatePetTaxiPriceMutation();

  const [
    triggerGetPetProfile,
    { isLoading: isLoadingPets },
  ] = useLazyGetPetProfileQuery();

  const [
    createUserProfile,
    { isLoading: isCreatingPet },
  ] = useCreateUserProfileMutation();

  const [
    uploadFile,
    { isLoading: isUploadingImage },
  ] = useUploadCustomerFileMutation();

  // ==========================================================
  // PET STATE
  // ==========================================================

  const [pets, setPets] =
    useState<PetData[]>([]);

  const [
    calculatedPriceData,
    setCalculatedPriceData,
  ] =
    useState<ICalculatePettaxiPriceResponse | null>(
      null
    );

  const questionCardRef =
    useRef<HTMLDivElement>(null);

  // ==========================================================
  // STEPPER
  // ==========================================================

  const [currentStep, setCurrentStep] =
    useState<number>(() => {
      if (
        localStorage.getItem(
          "selected_active_pet"
        )
      ) {
        return 2;
      }

      if (
        localStorage.getItem(
          "selected_pet_category"
        )
      ) {
        return 1;
      }

      return 0;
    });

  const [
    selectedPetCategory,
    setSelectedPetCategory,
  ] = useState<string | undefined>(() => {
    return (
      localStorage.getItem(
        "selected_pet_category"
      ) || undefined
    );
  });

  const [
    selectedSavedPet,
    setSelectedSavedPet,
  ] = useState<
    PetData | undefined
  >(() => {
    const savedActive =
      localStorage.getItem(
        "selected_active_pet"
      );

    return savedActive
      ? JSON.parse(savedActive)
      : undefined;
  });

  const [questionStep, setQuestionStep] =
    useState(0);

  const [
    showQuestionCard,
    setShowQuestionCard,
  ] = useState(false);

  // ==========================================================
  // PICKUP ADDRESS
  // ==========================================================

  const [latitude, setLatitude] =
    useState(defaultCenter.lat);

  const [longitude, setLongitude] =
    useState(defaultCenter.lng);

  const [city, setCity] =
    useState("");

  const [stateName, setStateName] =
    useState("");

  const [country, setCountry] =
    useState("United Arab Emirates");

  const [address, setAddress] =
    useState("");

  const [addressLine2, setAddressLine2] =
    useState("");

  // ==========================================================
  // DROP ADDRESS
  // ==========================================================

  const [dropAddress, dropSetAddress] =
    useState("");

  const [
    dropAddressLine2,
    dropSetAddressLine2,
  ] = useState("");

  const [
    droplatitude,
    dropSetLatitude,
  ] = useState(defaultCenter.lat);

  const [
    dropLongitude,
    dropSetLongitude,
  ] = useState(defaultCenter.lng);

  const [dropCity, dropSetCity] =
    useState("");

  const [
    dropStateName,
    dropSetStateName,
  ] = useState("");

  const [
    dropCountry,
    dropSetCountry,
  ] = useState("United Arab Emirates");

  // ==========================================================
  // ANSWERS
  // ==========================================================
  //
  // NOTE:
  //
  // `weight` currently stores the selected SIZE option.
  //
  // Example:
  //
  // answers.weight = "large"
  //
  // It does NOT contain "25" or "30".
  //
  // ==========================================================

  const [answers, setAnswers] =
    useState<{
      petType: string;
      petName: string;
      breed: string;
      gender: string;
      weight: string;
      petImage: string | File;
    }>({
      petType: "",
      petName: "",
      breed: "",
      gender: "",
      weight: "",
      petImage: "",
    });

  // ==========================================================
  // FETCH PET PROFILES
  // ==========================================================

  const fetchPetProfiles = async () => {
    try {
      const response =
        await triggerGetPetProfile().unwrap();

      if (
        response &&
        response.data
      ) {
        setPets(response.data);
      }
    } catch (error) {
      console.error(
        "Failed to fetch pet profiles:",
        error
      );

      setPets([]);
    }
  };

  useEffect(() => {
    fetchPetProfiles();
  }, []);

  // ==========================================================
  // QUESTIONS
  // ==========================================================

  const rawQuestions =
    getBookingQuestions(
      answers.petType
    );

  const bookingQuestions =
    rawQuestions.filter(
      (q) =>
        !(
          answers.petType === "cat" &&
          q.key === "weight"
        )
    );

  // ==========================================================
  // PRICE CALCULATION
  // ==========================================================

  useEffect(() => {
    if (
      latitude &&
      longitude &&
      droplatitude &&
      dropLongitude
    ) {
      handleCalculatePrice();
    }
  }, [
    latitude,
    longitude,
    droplatitude,
    dropLongitude,
  ]);

  const handleCalculatePrice =
    async () => {
      const payload: IPetTaxiPriceCalculateRequest =
        {
          pickupEmirate:
            stateName ||
            city ||
            "Dubai",

          pickupLatitude:
            latitude,

          pickupLongitude:
            longitude,

          dropEmirate:
            dropStateName ||
            dropCity ||
            "Dubai",

          dropLatitude:
            droplatitude,

          dropLongitude:
            dropLongitude,
        };

      if (dropStateName) {
        try {
          const response =
            await calculatePetTaxiPrice(
              payload
            ).unwrap();

          setCalculatedPriceData(
            response
          );
        } catch (err) {
          console.error(
            "Price calculation failed:",
            err
          );
        }
      }
    };

  // ==========================================================
  // REVERSE GEOCODING
  // ==========================================================

  const reverseGeocode = async (
    lat: number,
    lng: number,
    isPickup: boolean
  ) => {
    try {
      const response =
        await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${MAPS_KEY}`
        );

      const data =
        await response.json();

      if (
        data.status === "OK" &&
        data.results?.[0]
      ) {
        const place =
          data.results[0];

        const components =
          place.address_components ||
          [];

        const getComponent = (
          type: string
        ) =>
          components.find(
            (c: any) =>
              c.types.includes(type)
          )?.long_name || "";

        if (isPickup) {
          setAddress(
            place.formatted_address ||
              ""
          );

          setCity(
            getComponent("locality")
          );

          setStateName(
            getComponent(
              "administrative_area_level_1"
            )
          );

          setCountry(
            getComponent(
              "country"
            ) ||
              "United Arab Emirates"
          );

          setLatitude(lat);

          setLongitude(lng);
        } else {
          dropSetAddress(
            place.formatted_address ||
              ""
          );

          dropSetCity(
            getComponent("locality")
          );

          dropSetStateName(
            getComponent(
              "administrative_area_level_1"
            )
          );

          dropSetCountry(
            getComponent(
              "country"
            ) ||
              "United Arab Emirates"
          );

          dropSetLatitude(lat);

          dropSetLongitude(lng);
        }
      }
    } catch (error) {
      console.error(
        "Geocoding failed:",
        error
      );
    }
  };

  // ==========================================================
  // FILTER PETS
  // ==========================================================

  const profilesFilteredByCategory =
    pets
      .filter((pet) => {
        if (!selectedPetCategory) {
          return true;
        }

        const selectedType =
          String(
            selectedPetCategory
          )
            .trim()
            .toLowerCase();

        const petType =
          Number(pet.petType) === 2
            ? "cat"
            : Number(pet.petType) === 1
            ? "dog"
            : String(
                pet.petType || ""
              )
                .trim()
                .toLowerCase();

        return (
          petType === selectedType
        );
      })
      .map((pet) => {
        const isCat =
          Number(pet.petType) === 2 ||
          String(
            pet.petType
          )
            .trim()
            .toLowerCase() ===
            "cat";

        return {
          ...pet,

          id: pet.id,

          petName:
            pet.petName,

          name:
            pet.petName,

          petType:
            isCat ? "cat" : "dog",

          photoUrl:
            pet.photoUrl ||
            (isCat
              ? CAT_CARTOON_AVATARS[0]
                  .value
              : DOG_CARTOON_AVATARS[0]
                  .value),

          image:
            pet.photoUrl,

          // IMPORTANT:
          // Preserve backend breedSize.
          breedSize:
            pet.breedSize ||
            "standard",
        };
      });

  // ==========================================================
  // CATEGORY SELECT
  // ==========================================================

  const handleCategorySelect =
    (categoryItem: any) => {
      const categoryValue =
        categoryItem?.value ||
        categoryItem?.id;

      setSelectedPetCategory(
        categoryValue
      );

      setSelectedSavedPet(
        undefined
      );

      setShowQuestionCard(
        false
      );

      if (categoryValue) {
        localStorage.setItem(
          "selected_pet_category",
          categoryValue
        );

        setCurrentStep(1);
      } else {
        localStorage.removeItem(
          "selected_pet_category"
        );
      }

      localStorage.removeItem(
        "selected_active_pet"
      );

      window.scrollTo({
        top: 0,
        behavior: "auto",
      });
    };

  // ==========================================================
  // SAVED PET SELECT
  // ==========================================================

  const handleSavedPetSelect =
    (pet: any) => {
      const originalPet =
        pets.find(
          (p) =>
            p.id === pet.id
        ) || pet;

      setSelectedSavedPet(
        originalPet
      );

      localStorage.setItem(
        "selected_active_pet",
        JSON.stringify(
          originalPet
        )
      );

      setCurrentStep(2);

      window.scrollTo({
        top: 0,
        behavior: "auto",
      });
    };

  // ==========================================================
  // STEP NAVIGATION
  // ==========================================================

  const handleStepHeaderNavigation =
    (targetStep: number) => {
      if (targetStep === 0) {
        setSelectedPetCategory(
          undefined
        );

        setSelectedSavedPet(
          undefined
        );

        setShowQuestionCard(
          false
        );

        localStorage.removeItem(
          "selected_pet_category"
        );

        localStorage.removeItem(
          "selected_active_pet"
        );

        setCurrentStep(0);
      } else if (
        targetStep === 1 &&
        selectedPetCategory
      ) {
        setShowQuestionCard(
          false
        );

        setCurrentStep(1);
      }

      window.scrollTo({
        top: 0,
        behavior: "auto",
      });
    };

  // ==========================================================
  // QUESTION ANSWER
  // ==========================================================

  const handleAnswer = (
    value: any
  ) => {
    const currentQuestion =
      bookingQuestions[
        questionStep
      ];

    if (!currentQuestion) {
      return;
    }

    setAnswers(
      (prev) => ({
        ...prev,
        [currentQuestion.key]:
          value,
      })
    );
  };

  // ==========================================================
  // CREATE PET
  // ==========================================================

  const handleContinue =
    async () => {
      if (
        questionStep <
        bookingQuestions.length - 1
      ) {
        setQuestionStep(
          (prev) => prev + 1
        );

        return;
      }

      try {
        const activePetType =
          answers.petType ||
          selectedPetCategory ||
          "dog";

        // ====================================================
        // IMAGE
        // ====================================================

        let finalPhotoUrl =
          activePetType === "cat"
            ? CAT_CARTOON_AVATARS[0]
                .value
            : DOG_CARTOON_AVATARS[0]
                .value;

        if (
          answers.petImage instanceof
          File
        ) {
          const uploadRes =
            await uploadFile({
              File: answers.petImage,
              FolderName:
                "CMS/EditorImages",
            }).unwrap();

          if (uploadRes) {
            finalPhotoUrl =
              typeof uploadRes ===
              "string"
                ? uploadRes
                : (uploadRes as any)
                    .fileUrl ||
                  (uploadRes as any)
                    .url ||
                  finalPhotoUrl;
          }
        } else if (
          typeof answers.petImage ===
            "string" &&
          answers.petImage.trim() !==
            ""
        ) {
          finalPhotoUrl =
            answers.petImage;
        }

        // ====================================================
        // PET TYPE
        // ====================================================

        const petTypeNumeric =
          activePetType === "cat"
            ? 2
            : 1;

        // ====================================================
        // GENDER
        // ====================================================
        //
        // Question options:
        //
        // 1 = Male
        // 2 = Female
        //
        // ====================================================

        const genderNumeric =
          answers.gender === "2"
            ? 2
            : 1;

        // ====================================================
        // BREED SIZE
        // ====================================================
        //
        // IMPORTANT:
        //
        // DO NOT use:
        //
        // parseFloat(answers.weight)
        //
        // DO NOT do:
        //
        // weight > 15 ? "large" : "small"
        //
        // The selected option itself determines breedSize.
        //
        // ====================================================

        const breedSize =
          BREED_SIZE_MAP[
            answers.weight
          ] || "medium";

        // ====================================================
        // WEIGHT
        // ====================================================
        //
        // The current UI is selecting SIZE, not exact weight.
        //
        // Therefore breedSize is completely independent from
        // this field.
        //
        // ====================================================

        const weightNumeric = 0;

        // ====================================================
        // PAYLOAD
        // ====================================================

        const payload: ICreatePetProfileRequest =
          {
            currentSubmittedStep: 1,

            isFullyFilled: true,

            petName:
              answers.petName ||
              "Pet",

            petType:
              petTypeNumeric,

            breed:
              answers.breed ||
              "Standard",

            gender:
              genderNumeric,

            weight:
              weightNumeric,

            dateType:
              "DOB",

            petDate:
              new Date().toISOString(),

            furColor:
              "Standard",

            activityLevel:
              "Medium",

            personality: [],

            foodPreferences: [],

            dietFrequency:
              "Twice daily",

            portionSize:
              "Medium",

            favouriteTreats: [],

            noKnownIssues:
              true,

            healthConditions: [],

            requiresVetConsultationAlert:
              false,

            groomingFrequency:
              "Monthly",

            sheddingFrequency:
              "Moderate",

            // DIRECT SIZE
            breedSize:
              breedSize,

            photoUrl:
              finalPhotoUrl,
          };

        // Debug
        console.log(
          "CREATE PET TAXI PAYLOAD:",
          payload
        );

        // ====================================================
        // CREATE
        // ====================================================

        const response =
          await createUserProfile(
            payload
          ).unwrap();

        if (
          response?.success &&
          response?.data
        ) {
          const createdPet =
            response.data;

          // Refresh pets
          await fetchPetProfiles();

          // Select newly created pet
          setSelectedSavedPet(
            createdPet
          );

          setShowQuestionCard(
            false
          );

          // Save active pet
          localStorage.setItem(
            "selected_active_pet",
            JSON.stringify(
              createdPet
            )
          );

          // Save category
          if (answers.petType) {
            localStorage.setItem(
              "selected_pet_category",
              answers.petType
            );

            setSelectedPetCategory(
              answers.petType
            );
          }

          // Reset form
          setAnswers({
            petType: "",
            petName: "",
            breed: "",
            gender: "",
            weight: "",
            petImage: "",
          });

          setQuestionStep(0);

          setCurrentStep(2);

          window.scrollTo({
            top: 0,
            behavior: "auto",
          });
        }
      } catch (error) {
        console.error(
          "Failed to create pet profile:",
          error
        );

        if (isLoggedIn) {
          showConfirm({
            title: "Error",

            message:
              "Failed to create pet profile. Please try again.",

            type: "error",

            confirmText:
              "Close",

            showCancel:
              false,
          });
        }
      }
    };

  // ==========================================================
  // CURRENT QUESTION
  // ==========================================================

  const currentQuestion =
    bookingQuestions[
      questionStep
    ];

  let currentOptions =
    currentQuestion?.options ||
    [];

  const activePetType =
    answers.petType ||
    selectedPetCategory ||
    "dog";

  if (
    currentQuestion?.key ===
    "breed"
  ) {
    currentOptions =
      activePetType === "dog"
        ? dogbreedOptionsMap
        : catbreedOptionsMap;
  } else if (
    currentQuestion?.key ===
    "petImage"
  ) {
    currentOptions =
      activePetType === "cat"
        ? CAT_CARTOON_AVATARS
        : DOG_CARTOON_AVATARS;
  }

  // ==========================================================
  // ADD PET
  // ==========================================================

  const handleAddPetClick =
    () => {
      setShowQuestionCard(
        true
      );

      setQuestionStep(0);

      setAnswers(
        (prev) => ({
          ...prev,

          petType:
            selectedPetCategory ||
            "",
        })
      );

      setTimeout(() => {
        questionCardRef.current?.scrollIntoView(
          {
            behavior:
              "smooth",

            block:
              "center",
          }
        );
      }, 150);
    };

  // ==========================================================
  // BOOKING
  // ==========================================================

  const handleBooking =
    () => {
      if (!selectedSavedPet) {
        showConfirm({
          title:
            "⚠️ Missing Selection",

          message:
            "Please select or add a pet profile before moving ahead.",

          type:
            "warning",

          confirmText:
            "Understood",

          showCancel:
            false,
        });

        return;
      }

      if (
        !address ||
        !dropAddress
      ) {
        showConfirm({
          title:
            "📍 Address Configuration Required",

          message:
            "Both pickup and dropoff destinations must be set to compute distance fare.",

          type:
            "warning",

          confirmText:
            "Complete Address",

          showCancel:
            false,
        });

        return;
      }

      if (!calculatedPriceData) {
        showConfirm({
          title:
            "⌛ Price Calculation Pending",

          message:
            "Please wait while we finish calculating your route fare estimate.",

          type:
            "warning",

          confirmText:
            "Understood",

          showCancel:
            false,
        });

        return;
      }

      navigate(
        "/customergroomingbookingdatescreen",
        {
          state: {
            serviceType:
              "taxi",

            serviceId:
              27,

            bookingData: {
              petType:
                selectedPetCategory,

              pet:
                selectedSavedPet,

              breed:
                selectedSavedPet?.breed ||
                "Standard",

              weight:
                selectedSavedPet?.weight ||
                "Standard",

              // IMPORTANT:
              // Also pass the saved breed size.
              breedSize:
                selectedSavedPet?.breedSize ||
                "standard",

              pickup: {
                address,

                addressLine2,

                city,

                state:
                  stateName,

                country,

                latitude,

                longitude,
              },

              drop: {
                address:
                  dropAddress,

                addressLine2:
                  dropAddressLine2,

                city:
                  dropCity,

                state:
                  dropStateName,

                country:
                  dropCountry,

                latitude:
                  droplatitude,

                longitude:
                  dropLongitude,
              },

              pricing:
                calculatedPriceData,
            },
          },
        }
      );
    };

  // ==========================================================
  // STEPS
  // ==========================================================

  const stepsData = [
    {
      label:
        "Select Pet Type",
    },
    {
      label:
        "Select Pet Profile",
    },
    {
      label:
        "Address Configurations",
    },
  ];

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <div className="pb-20 bg-white w-full min-h-screen">

      <div className="max-w-7xl mx-auto px-4 relative z-30 pt-6">

        {/* ==================================================
            STEPPER
        ================================================== */}

        <div className="bg-white rounded-3xl p-5 shadow-xl shadow-gray-100/40 border border-gray-100 mb-8 max-w-5xl mx-auto">

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 md:gap-2">

            {stepsData.map(
              (
                stepItem,
                idx
              ) => {

                const isCompleted =
                  currentStep >
                  idx;

                const isActive =
                  currentStep ===
                  idx;

                const isClickable =
                  idx <
                  currentStep;

                return (
                  <React.Fragment
                    key={idx}
                  >

                    <div
                      onClick={() =>
                        isClickable &&
                        handleStepHeaderNavigation(
                          idx
                        )
                      }
                      className={`flex items-center gap-3 select-none ${
                        isClickable
                          ? "cursor-pointer group"
                          : "cursor-default"
                      }`}
                    >

                      <div
                        className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-300 ${
                          isCompleted
                            ? "bg-emerald-500 text-white"
                            : isActive
                            ? "bg-mainBrandColor text-white ring-4 ring-orange-100"
                            : "bg-gray-100 text-gray-400"
                        }`}
                      >

                        {isCompleted ? (
                          <Check
                            size={16}
                            strokeWidth={
                              3
                            }
                          />
                        ) : (
                          idx + 1
                        )}

                      </div>

                      <div>
                        <h4
                          className={`text-sm font-semibold transition-colors ${
                            isActive
                              ? "text-mainBrandColor"
                              : isCompleted
                              ? "text-gray-800 group-hover:text-mainBrandColor"
                              : "text-gray-400"
                          }`}
                        >
                          {
                            stepItem.label
                          }
                        </h4>
                      </div>

                    </div>

                    {idx <
                      2 && (
                      <div
                        className={`hidden md:block h-[2px] flex-1 mx-4 rounded-full transition-colors duration-500 ${
                          currentStep >
                          idx
                            ? "bg-emerald-500"
                            : "bg-gray-100"
                        }`}
                      />
                    )}

                  </React.Fragment>
                );
              }
            )}

          </div>
        </div>

        {/* ==================================================
            SLIDING CANVAS
        ================================================== */}

        <div
          className={`relative w-full ${
            currentStep === 1
              ? "overflow-visible"
              : "overflow-hidden"
          }`}
        >

          <div
            className="flex transition-transform duration-500 ease-in-out w-[300%]"
            style={{
              transform:
                `translateX(-${
                  (currentStep * 100) /
                  3
                }%)`,
            }}
          >

            {/* =================================================
                STEP 0
            ================================================= */}

            <div
              className={`w-full px-2 ${
                currentStep !== 0
                  ? "h-0 overflow-hidden"
                  : "h-auto"
              }`}
            >

              <div className="bg-white flex items-center justify-center rounded-3xl px-6 py-5 shadow-xl shadow-gray-100/40 border border-gray-100 w-full">

                <SelectPetsSection
                  title="Select Pet Type Category"
                  subtitle="Choose the companion"

                  data={petOptions.filter(
                    (pet: any) => {
                      const value =
                        String(
                          pet.value ||
                            pet.id ||
                            ""
                        )
                          .trim()
                          .toLowerCase();

                      return (
                        value ===
                          "dog" ||
                        value ===
                          "cat"
                      );
                    }
                  )}

                  selectedId={
                    selectedPetCategory
                  }

                  onSelect={
                    handleCategorySelect
                  }

                  showAddButton={
                    false
                  }

                  textContainer="flex items-center justify-center"
                />

              </div>
            </div>

            {/* =================================================
                STEP 1
            ================================================= */}

            <div
              className={`w-full px-2 space-y-6 ${
                currentStep !== 1
                  ? "h-0 overflow-hidden"
                  : "h-auto overflow-visible"
              }`}
            >

              <div className="space-y-6">

                {selectedPetCategory &&
                  !showQuestionCard && (
                    <div className="bg-white flex items-center justify-center rounded-3xl px-6 py-5 shadow-xl shadow-gray-100/40 border border-gray-100">

                      {isLoadingPets ? (
                        <div className="py-10 text-center text-gray-400">
                          Loading your pet
                          profiles...
                        </div>
                      ) : (
                        <SelectPetsSection
                          title="Who's Travelling with us?"
                          subtitle=""
                          data={
                            profilesFilteredByCategory
                          }
                          selectedId={
                            selectedSavedPet?.id
                          }
                          onSelect={
                            handleSavedPetSelect
                          }
                          showAddButton={
                            true
                          }
                          onAddClick={
                            handleAddPetClick
                          }
                          textContainer="flex items-center justify-center"
                        />
                      )}

                    </div>
                  )}

                <div
                  ref={
                    questionCardRef
                  }
                  className="scroll-mt-6"
                >

                  {showQuestionCard &&
                    bookingQuestions[
                      questionStep
                    ] && (
                      <QuestionCard

                        currentStep={
                          questionStep +
                          1
                        }

                        totalSteps={
                          bookingQuestions.length
                        }

                        question={
                          bookingQuestions[
                            questionStep
                          ].question
                        }

                        type={
                          bookingQuestions[
                            questionStep
                          ].type as QuestionType
                        }

                        options={
                          currentOptions
                        }

                        value={
                          answers[
                            bookingQuestions[
                              questionStep
                            ]
                              .key as keyof typeof answers
                          ] as any
                        }

                        onChange={
                          handleAnswer
                        }

                        onContinue={
                          handleContinue
                        }

                        disabled={
                          isCreatingPet ||
                          isUploadingImage
                        }

                        onBack={() => {
                          if (
                            questionStep >
                            0
                          ) {
                            setQuestionStep(
                              (
                                prev
                              ) =>
                                prev -
                                1
                            );
                          } else {
                            setShowQuestionCard(
                              false
                            );
                          }
                        }}
                      />
                    )}

                </div>

              </div>
            </div>

            {/* =================================================
                STEP 2
            ================================================= */}

            <div
              className={`w-full px-2 space-y-8 ${
                currentStep !== 2
                  ? "h-0 overflow-hidden"
                  : "h-auto"
              }`}
            >

              <div className="space-y-8">

                {/* =================================================
                    PICKUP
                ================================================= */}

                <div className="bg-white rounded-[2rem] px-6 py-5 shadow-xl shadow-gray-100/40 border border-gray-100 text-left">

                  <h3 className="text-xl md:text-2xl font-bold text-iconColor mb-3">
                    Address Information
                    (Pickup Location)
                  </h3>

                  <p className="text-xs text-slate-400 mb-4">
                    Click on the map or
                    search to define your
                    pickup point.
                  </p>

                  {/* MAP */}

                  {isLoaded && (
                    <div className="mb-4 rounded-xl overflow-hidden border border-slate-200">

                      <GoogleMap
                        mapContainerStyle={
                          containerStyle
                        }

                        center={{
                          lat: latitude,
                          lng: longitude,
                        }}

                        zoom={14}

                        onClick={(e) => {
                          if (
                            e.latLng
                          ) {
                            reverseGeocode(
                              e.latLng.lat(),
                              e.latLng.lng(),
                              true
                            );
                          }
                        }}
                      >

                        <MarkerF
                          position={{
                            lat: latitude,
                            lng: longitude,
                          }}

                          draggable={
                            true
                          }

                          onDragEnd={(
                            e
                          ) => {
                            if (
                              e.latLng
                            ) {
                              reverseGeocode(
                                e.latLng.lat(),
                                e.latLng.lng(),
                                true
                              );
                            }
                          }}
                        />

                      </GoogleMap>

                    </div>
                  )}

                  {/* ADDRESS SEARCH */}

                  <div className="mb-4">

                    <label className="text-sm font-bold text-mainBrandColor mb-2 block">
                      Address Search
                    </label>

                    <div className="relative">

                      <MapPin
                        size={18}
                        className="absolute left-4 top-4 text-[#6DBE45] z-10"
                      />

                      <Autocomplete
                        apiKey={
                          MAPS_KEY
                        }

                        className="w-full border rounded-xl py-3.5 pl-12 pr-4 bg-gray-50/40 outline-none focus:border-mainBrandColor transition-all text-xs"

                        placeholder="Search Pickup Location Address..."

                        defaultValue={
                          address
                        }

                        key={
                          address
                        }

                        options={{
                          componentRestrictions:
                            {
                              country:
                                "ae",
                            },

                          types: [
                            "geocode",
                          ],
                        }}

                        onPlaceSelected={(
                          place: any
                        ) => {
                          const components =
                            place.address_components ||
                            [];

                          const getComponent =
                            (
                              type: string
                            ) =>
                              components.find(
                                (
                                  c: any
                                ) =>
                                  c.types.includes(
                                    type
                                  )
                              )
                                ?.long_name ||
                              "";

                          setAddress(
                            place.formatted_address ||
                              ""
                          );

                          setCity(
                            getComponent(
                              "locality"
                            )
                          );

                          setStateName(
                            getComponent(
                              "administrative_area_level_1"
                            )
                          );

                          setCountry(
                            getComponent(
                              "country"
                            ) ||
                              "United Arab Emirates"
                          );

                          if (
                            place
                              .geometry
                              ?.location
                          ) {
                            setLatitude(
                              typeof place
                                .geometry
                                .location
                                .lat ===
                                "function"
                                ? place
                                    .geometry
                                    .location
                                    .lat()
                                : place
                                    .geometry
                                    .location
                                    .lat
                            );

                            setLongitude(
                              typeof place
                                .geometry
                                .location
                                .lng ===
                                "function"
                                ? place
                                    .geometry
                                    .location
                                    .lng()
                                : place
                                    .geometry
                                    .location
                                    .lng
                            );
                          }
                        }}
                      />

                    </div>
                  </div>

                  {/* PICKUP DETAILS */}

                  <div className="grid md:grid-cols-2 gap-4">

                    <TextInputComp
                      value={
                        addressLine2
                      }
                      onChangeText={
                        setAddressLine2
                      }
                      placeholder="e.g. Apt 302, Floor 3"
                      label="Apartment / Unit No."
                      showLabel
                    />

                    <TextInputComp
                      value={
                        city
                      }
                      onChangeText={
                        setCity
                      }
                      placeholder="City"
                      label="City"
                      showLabel
                    />

                    <TextInputComp
                      value={
                        stateName
                      }
                      onChangeText={
                        setStateName
                      }
                      placeholder="State"
                      label="State"
                      showLabel
                    />

                    <TextInputComp
                      value={
                        country
                      }
                      onChangeText={
                        setCountry
                      }
                      placeholder="Country"
                      label="Country"
                      showLabel
                    />

                  </div>

                </div>

                {/* =================================================
                    DROP
                ================================================= */}

                <div className="bg-white rounded-[2rem] p-6 md:p-8 shadow-xl shadow-gray-100/40 border border-gray-100 text-left">

                  <h3 className="text-xl md:text-2xl font-bold text-iconColor mb-3">
                    Address Information
                    (Drop Location)
                  </h3>

                  <p className="text-xs text-slate-400 mb-4">
                    Click on the map or
                    search to define your
                    destination drop point.
                  </p>

                  {/* DROP MAP */}

                  {isLoaded && (
                    <div className="mb-4 rounded-xl overflow-hidden border border-slate-200">

                      <GoogleMap
                        mapContainerStyle={
                          containerStyle
                        }

                        center={{
                          lat: droplatitude,
                          lng: dropLongitude,
                        }}

                        zoom={14}

                        onClick={(e) => {
                          if (
                            e.latLng
                          ) {
                            reverseGeocode(
                              e.latLng.lat(),
                              e.latLng.lng(),
                              false
                            );
                          }
                        }}
                      >

                        <MarkerF
                          position={{
                            lat: droplatitude,
                            lng: dropLongitude,
                          }}

                          draggable={
                            true
                          }

                          onDragEnd={(
                            e
                          ) => {
                            if (
                              e.latLng
                            ) {
                              reverseGeocode(
                                e.latLng.lat(),
                                e.latLng.lng(),
                                false
                              );
                            }
                          }}
                        />

                      </GoogleMap>

                    </div>
                  )}

                  {/* DROP SEARCH */}

                  <div className="mb-4">

                    <label className="text-sm font-bold text-mainBrandColor mb-2 block">
                      Address Search
                    </label>

                    <div className="relative">

                      <MapPin
                        size={18}
                        className="absolute left-4 top-4 text-red-500 z-10"
                      />

                      <Autocomplete
                        apiKey={
                          MAPS_KEY
                        }

                        className="w-full border rounded-xl py-3.5 pl-12 pr-4 bg-gray-50/40 outline-none focus:border-mainBrandColor transition-all text-xs"

                        placeholder="Search Destination Drop Location Address..."

                        defaultValue={
                          dropAddress
                        }

                        key={
                          dropAddress
                        }

                        options={{
                          componentRestrictions:
                            {
                              country:
                                "ae",
                            },

                          types: [
                            "geocode",
                          ],
                        }}

                        onPlaceSelected={(
                          place: any
                        ) => {
                          const components =
                            place.address_components ||
                            [];

                          const getComponent =
                            (
                              type: string
                            ) =>
                              components.find(
                                (
                                  c: any
                                ) =>
                                  c.types.includes(
                                    type
                                  )
                              )
                                ?.long_name ||
                              "";

                          dropSetAddress(
                            place.formatted_address ||
                              ""
                          );

                          dropSetCity(
                            getComponent(
                              "locality"
                            )
                          );

                          dropSetStateName(
                            getComponent(
                              "administrative_area_level_1"
                            )
                          );

                          dropSetCountry(
                            getComponent(
                              "country"
                            ) ||
                              "United Arab Emirates"
                          );

                          if (
                            place
                              .geometry
                              ?.location
                          ) {
                            dropSetLatitude(
                              typeof place
                                .geometry
                                .location
                                .lat ===
                                "function"
                                ? place
                                    .geometry
                                    .location
                                    .lat()
                                : place
                                    .geometry
                                    .location
                                    .lat
                            );

                            dropSetLongitude(
                              typeof place
                                .geometry
                                .location
                                .lng ===
                                "function"
                                ? place
                                    .geometry
                                    .location
                                    .lng()
                                : place
                                    .geometry
                                    .location
                                    .lng
                            );
                          }
                        }}
                      />

                    </div>
                  </div>

                  {/* DROP DETAILS */}

                  <div className="grid md:grid-cols-2 gap-4">

                    <TextInputComp
                      value={
                        dropAddressLine2
                      }
                      onChangeText={
                        dropSetAddressLine2
                      }
                      placeholder="e.g. Villa 12, Office Suite"
                      label="Apartment / Unit No."
                      showLabel
                    />

                    <TextInputComp
                      value={
                        dropCity
                      }
                      onChangeText={
                        dropSetCity
                      }
                      placeholder="City"
                      label="City"
                      showLabel
                    />

                    <TextInputComp
                      value={
                        dropStateName
                      }
                      onChangeText={
                        dropSetStateName
                      }
                      placeholder="State"
                      label="State"
                      showLabel
                    />

                    <TextInputComp
                      value={
                        dropCountry
                      }
                      onChangeText={
                        dropSetCountry
                      }
                      placeholder="Country"
                      label="Country"
                      showLabel
                    />

                  </div>

                </div>

                {/* =================================================
                    PRICE
                ================================================= */}

                {(
                  isCalculatingPrice ||
                  calculatedPriceData
                ) && (
                  <div className="bg-emerald-50/60 rounded-2xl p-6 border border-emerald-200/80 shadow-xs max-w-xl mx-auto space-y-3">

                    <div className="flex items-center gap-2 text-emerald-800 font-extrabold text-base">

                      <Calculator
                        size={20}
                        className="text-[#6DBE45]"
                      />

                      <span>
                        Estimated Taxi
                        Fare Breakdown
                      </span>

                    </div>

                    {isCalculatingPrice ? (
                      <div className="flex items-center justify-center py-4 gap-2 text-xs font-bold text-slate-500">

                        <Loader2
                          className="animate-spin text-[#6DBE45]"
                          size={18}
                        />

                        Calculating fare
                        according to route
                        distance...

                      </div>
                    ) : (
                      calculatedPriceData && (
                        <div className="space-y-3 text-xs font-semibold text-slate-700 pt-1">

                          {/* DISTANCE */}

                          <div className="flex justify-between">

                            <span>
                              Total Route
                              Distance
                            </span>

                            <span className="font-bold text-slate-900">
                              {calculatedPriceData.distanceKm.toFixed(
                                2
                              )}{" "}
                              km
                            </span>

                          </div>

                          {/* BASE FARE */}

                          <div className="flex justify-between">

                            <span>
                              Base Fare
                            </span>

                            <span className="font-bold text-slate-900">
                              AED{" "}
                              {calculatedPriceData.baseFare.toFixed(
                                2
                              )}
                            </span>

                          </div>

                          {/* FARE INFORMATION */}

                          <div className="mt-3 rounded-xl bg-white/70 border border-emerald-100 p-4 space-y-2">

                            <div className="text-[11px] font-extrabold text-emerald-800 uppercase tracking-wide mb-2">
                              Fare Information
                            </div>

                            <div className="flex justify-between">

                              <span>
                                Included
                                Distance
                              </span>

                              <span className="font-bold text-slate-900">
                                {
                                  calculatedPriceData.includedKm
                                }{" "}
                                km
                              </span>

                            </div>

                            <div className="flex justify-between">

                              <span>
                                Extra Distance
                                Rate
                              </span>

                              <span className="font-bold text-slate-900">
                                AED{" "}
                                {calculatedPriceData.extraKmRate.toFixed(
                                  2
                                )}{" "}
                                / km
                              </span>

                            </div>

                            <div className="flex justify-between">

                              <span>
                                Additional
                                Distance
                              </span>

                              <span className="font-bold text-slate-900">
                                {calculatedPriceData.additionalDistanceKm.toFixed(
                                  2
                                )}{" "}
                                km
                              </span>

                            </div>

                            <div className="flex justify-between">

                              <span>
                                Additional
                                Distance Charge
                              </span>

                              <span className="font-bold text-slate-900">
                                AED{" "}
                                {calculatedPriceData.additionalCharge.toFixed(
                                  2
                                )}
                              </span>

                            </div>

                            <div className="flex justify-between">

                              <span>
                                Free Waiting
                                Time
                              </span>

                              <span className="font-bold text-slate-900">
                                {
                                  calculatedPriceData.freeWaitingMinutes
                                }{" "}
                                minutes
                              </span>

                            </div>

                          </div>

                          {/* EMIRATE FARES */}

                          <div className="mt-2 rounded-xl bg-white/70 border border-emerald-100 p-4 space-y-2">

                            <div className="text-[11px] font-extrabold text-emerald-800 uppercase tracking-wide mb-2">
                              Emirate Base
                              Fares
                            </div>

                            <div className="flex justify-between">

                              <span>
                                Dubai
                              </span>

                              <span className="font-bold text-slate-900">
                                AED{" "}
                                {calculatedPriceData.dubaiBaseFare.toFixed(
                                  2
                                )}
                              </span>

                            </div>

                            <div className="flex justify-between">

                              <span>
                                Sharjah
                              </span>

                              <span className="font-bold text-slate-900">
                                AED{" "}
                                {calculatedPriceData.sharjahBaseFare.toFixed(
                                  2
                                )}
                              </span>

                            </div>

                            <div className="flex justify-between">

                              <span>
                                Abu Dhabi
                              </span>

                              <span className="font-bold text-slate-900">
                                AED{" "}
                                {calculatedPriceData.abuDhabiBaseFare.toFixed(
                                  2
                                )}
                              </span>

                            </div>

                          </div>

                          {/* FINAL FARE */}

                          <div className="flex justify-between text-sm font-black text-[#2F5E2E] pt-3 border-t border-emerald-200">

                            <span>
                              Estimated Fare
                            </span>

                            <span>
                              AED{" "}
                              {calculatedPriceData.totalPrice.toFixed(
                                2
                              )}
                            </span>

                          </div>

                        </div>
                      )
                    )}

                  </div>
                )}

                {/* =================================================
                    BOOK
                ================================================= */}

                <div className="pt-2 max-w-sm mx-auto">

                  <CommonButton
                    text={
                      isCalculatingPrice
                        ? "Calculating Fare..."
                        : "Continue Booking"
                    }

                    disabled={
                      isCalculatingPrice
                    }

                    className="w-full py-1.5 rounded-2xl font-bold tracking-wide shadow-lg text-white bg-mainBrandColor transition-all duration-300 hover:scale-[1.01] active:scale-95 disabled:opacity-50"

                    onClick={
                      handleBooking
                    }
                  />

                </div>

              </div>
            </div>

          </div>
        </div>

      </div>
    </div>
  );
};

export default PetTaxiScreen;